import { useState } from "react";
import { useTranslation } from "react-i18next";

import { IntakeShell } from "./components/IntakeShell";
import { IntakeFooterNav } from "./components/IntakeFooterNav";
import { useIntakeCopy } from "./hooks/useIntakeCopy";

const CONSENT_FEATURE_KEYS = [
  "intake.consent.feature_camera",
  "intake.consent.feature_voice",
  "intake.consent.feature_private",
] as const;

type Props = {
  onAgree: () => void;
  onBack?: () => void;
};

export function IntakeConsent({ onAgree, onBack }: Props) {
  const { t, progressLabel } = useIntakeCopy();
  const [ackStep, setAckStep] = useState(0);

  const allAcknowledged = ackStep >= CONSENT_FEATURE_KEYS.length;

  const handlePrimary = () => {
    if (!allAcknowledged) {
      setAckStep((prev) => prev + 1);
      return;
    }
    onAgree();
  };

  return (
    <IntakeShell
      progressStep={1}
      progressTotal={5}
      progressLabel={progressLabel(1, 5)}
      footer={
        <IntakeFooterNav
          onBack={onBack}
          backLabel={t("intake.back")}
          primaryLabel={
            allAcknowledged
              ? t("intake.consent.lets_begin")
              : t("intake.consent.continue")
          }
          onPrimary={handlePrimary}
        />
      }
    >
      <h1 className="intake-flow__headline">{t("intake.consent.headline")}</h1>
      <p className="intake-flow__subtext intake-flow__subtext--center">
        {t("intake.consent.subtext")}
      </p>

      <div className="intake-consent__features">
        {CONSENT_FEATURE_KEYS.map((key, index) => {
          if (index > ackStep) return null;

          const isChecked = index < ackStep;
          const isCurrent = index === ackStep && !allAcknowledged;

          return (
            <div
              key={key}
              className={`intake-consent__feature${
                isCurrent ? " intake-consent__feature--current" : ""
              }${isChecked ? " intake-consent__feature--checked" : ""}`}
            >
              <span
                className={`intake-consent__icon${
                  isChecked ? " intake-consent__icon--checked" : ""
                }`}
              >
                {isChecked ? "✓" : index + 1}
              </span>
              <p>{t(key)}</p>
            </div>
          );
        })}
      </div>

      {allAcknowledged && (
        <p className="intake-consent__legal">{t("intake.consent.legal")}</p>
      )}
    </IntakeShell>
  );
}
